import React, { FC } from 'react'
import classNames from 'classnames'
import { Box, createStyles, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'

export interface ICardButtonProps {
  /**
   * @description the value to return to onClick
   */
  value: string

  /**
   * @description first line of text
   */
  line1: string

  /**
   * @description optional second line of text
   */
  line2?: string

  /**
   * @description triggers active state when equal to value
   */
  selected?: string

  /**
   * @description function called when the button is clicked, passes the value prop as an argument
   */
  onClick?: (value: string) => void

  className?: string
}

const useStyles = makeStyles(theme =>
  createStyles({
    root: {
      minHeight: '65px',
      padding: theme.spacing(1),
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
      alignItems: 'center',
      cursor: 'pointer',
      borderStyle: 'solid',
      borderWidth: '1px',
      borderColor: brandColors.coolGray4,
      userSelect: 'none',
      '&:hover': {
        borderColor: brandColors.skyBlue6,
      },
      '&:active': {
        background: brandColors.coolGray1,
        borderColor: brandColors.skyBlue6,
      },
    },
    selected: {
      background: brandColors.skyBlue2,
      borderColor: brandColors.skyBlue6,
    },
  })
)

export const CardButton: FC<ICardButtonProps> = ({
  className,
  line1,
  line2,
  onClick,
  selected,
  value,
}) => {
  const classes = useStyles()
  const usedClasses = classNames(classes.root, className, {
    [classes.selected]: value === selected,
  })

  const clickHandler = () => {
    if (onClick) {
      onClick(value)
    }
  }

  return (
    <Box className={usedClasses} onClick={clickHandler} data-test='card-button'>
      <Typography variant='subtitle1'>{line1}</Typography>
      {line2 && (
        <Typography variant='body2' color='textSecondary'>
          {line2}
        </Typography>
      )}
    </Box>
  )
}
